import {
	HUMAN_CHECK_ACTION,
	HUMAN_CHECK_HOSTNAMES,
	HumanCheckKind,
	RECAPTCHA_CHALLENGE_SITE_KEY,
	RECAPTCHA_SCORE_SITE_KEY,
	VerifyHumanResponse,
} from '@run-diffusion/shared';
import moment from 'moment-timezone';
import { functions } from '@/firebase';
import {
	getHumanCheckPass,
	removeHumanCheckPass,
	setHumanCheckPass,
	StoredHumanCheckPass,
} from '@/utils/localStorage';

/*
Client half of the Human Check. shared/src/humanCheck.ts has the whole flow; in short, the login page mints an
invisible score token, trades it for a pass with the `verifyHuman` callable, and the first sign-in capture hands
that pass to the server, which only grants the signup trial against a verified one.
 */

// The slice of the reCAPTCHA Enterprise JS API this app uses (there are no published typings for it).
export interface GrecaptchaEnterprise {
	ready: (callback: () => void) => void
	execute: (siteKey: string, options: { action: string }) => Promise<string>
	render: (container: HTMLElement, parameters: {
		'sitekey': string
		'theme'?: 'dark' | 'light'
		'callback'?: (token: string) => void
		'expired-callback'?: () => void
		'error-callback'?: () => void
	}) => number
	reset: (widgetId?: number) => void
}

export type VerifyHumanResult = 'passed' | 'failed' | 'rate-limited' | 'error';

// Where the login page's invisible check stands: not started, in flight, or settled with or without a pass.
export type HumanCheckBackgroundStatus = 'idle' | 'pending' | 'passed' | 'failed';

// recaptcha.net serves the same script as google.com and also loads where google.com is blocked (mainland China).
const SCRIPT_SRC_BASE: string = 'https://www.recaptcha.net/recaptcha/enterprise.js';
const SCRIPT_ID: string = 'recaptcha-enterprise-script';
const SCRIPT_LOAD_TIMEOUT_MS: number = 10000;
const EXECUTE_TIMEOUT_MS: number = 10000;
// A pass needs this much life left to start a sign-in: room for a slow OAuth popup, then the first sign-in capture.
const MIN_PASS_REMAINING_MINUTES: number = 5;

// On only where the site keys can work: a key is set and this page is served from a host the keys allow.
export const isHumanCheckConfigured = (): boolean => !!(
	(RECAPTCHA_SCORE_SITE_KEY || RECAPTCHA_CHALLENGE_SITE_KEY) &&
	HUMAN_CHECK_HOSTNAMES.includes(window.location.hostname)
);
export const isHumanCheckChallengeConfigured = (): boolean => !!RECAPTCHA_CHALLENGE_SITE_KEY;

const getEnterprise = (): GrecaptchaEnterprise | null => {
	const recaptchaWindow = window as unknown as { grecaptcha?: { enterprise?: GrecaptchaEnterprise } };
	return (recaptchaWindow.grecaptcha && recaptchaWindow.grecaptcha.enterprise) || null;
};

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => new Promise((resolve, reject) => {
	const timer: ReturnType<typeof setTimeout> = setTimeout(() => reject(new Error('reCAPTCHA timed out')), timeoutMs);
	promise.then(
		(value: T) => { clearTimeout(timer); resolve(value); },
		(error: unknown) => { clearTimeout(timer); reject(error); },
	);
});

let loadPromise: Promise<GrecaptchaEnterprise | null> | null = null;

// Loads enterprise.js once. Resolves null (never rejects) when it is blocked or slow; a later call tries again.
export const loadRecaptchaEnterprise = (): Promise<GrecaptchaEnterprise | null> => {
	if (loadPromise) return loadPromise;
	if (!isHumanCheckConfigured()) return Promise.resolve(null);

	const promise: Promise<GrecaptchaEnterprise | null> = new Promise((resolve) => {
		const alreadyLoaded: GrecaptchaEnterprise | null = getEnterprise();
		if (alreadyLoaded) {
			alreadyLoaded.ready(() => resolve(alreadyLoaded));
			return;
		}
		let settled: boolean = false;
		const finish = (value: GrecaptchaEnterprise | null) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			resolve(value);
		};
		const timer: ReturnType<typeof setTimeout> = setTimeout(() => finish(null), SCRIPT_LOAD_TIMEOUT_MS);
		const script: HTMLScriptElement = document.createElement('script');
		script.id = SCRIPT_ID;
		// render=<score key> also allows explicit render() of the challenge checkbox.
		script.src = `${SCRIPT_SRC_BASE}?render=${encodeURIComponent(RECAPTCHA_SCORE_SITE_KEY || 'explicit')}`;
		script.async = true;
		script.defer = true;
		script.onload = () => {
			const enterprise: GrecaptchaEnterprise | null = getEnterprise();
			if (!enterprise) {
				finish(null);
				return;
			}
			enterprise.ready(() => finish(enterprise));
		};
		script.onerror = () => finish(null);
		document.head.appendChild(script);
	});

	loadPromise = promise;
	promise.then((enterprise: GrecaptchaEnterprise | null) => {
		if (enterprise) return;
		// Let the next caller retry (e.g. after the person turns off an ad blocker)
		loadPromise = null;
		const script: HTMLElement | null = document.getElementById(SCRIPT_ID);
		if (script && script.parentNode) script.parentNode.removeChild(script);
	});
	return promise;
};

export const resetRecaptchaWidget = (widgetId: number): void => {
	const enterprise: GrecaptchaEnterprise | null = getEnterprise();
	if (enterprise) enterprise.reset(widgetId);
};

// In-memory copy so the pass still reaches the sign-in capture when the browser refuses localStorage.
let memoryPass: StoredHumanCheckPass | null = null;

const readPass = (): StoredHumanCheckPass | null => memoryPass || getHumanCheckPass();

const isPassValidForMinutes = (pass: StoredHumanCheckPass | null, minutes: number): boolean => (
	!!pass && moment(pass.expiresAtIso).isAfter(moment().add(minutes, 'minutes'))
);

// True when a stored pass can still carry a sign-in through to the server.
export const hasUsableHumanCheckPass = (): boolean => isPassValidForMinutes(readPass(), MIN_PASS_REMAINING_MINUTES);

// The pass id for captureSignInData, if an unexpired one is stored.
export const getHumanCheckPassIdForSignIn = (): string | null => {
	const pass: StoredHumanCheckPass | null = readPass();
	return isPassValidForMinutes(pass, 0) ? pass.passId : null;
};

export const clearHumanCheckPass = (): void => {
	memoryPass = null;
	removeHumanCheckPass();
};

// Trades a token for a pass. `error` means the callable itself failed, which is our outage, not a bot signal.
export const verifyHumanToken = async (kind: HumanCheckKind, token: string | null): Promise<VerifyHumanResult> => {
	try {
		const response: VerifyHumanResponse | null = (await functions.httpsCallable('verifyHuman')({ kind, token })).data || null;
		if (!response) return 'error';
		if (response.rateLimited) return 'rate-limited';
		if (!response.passed || !response.expiresAtIso) return 'failed';
		memoryPass = { passId: response.passId, expiresAtIso: response.expiresAtIso };
		setHumanCheckPass(memoryPass);
		return 'passed';
	} catch (e) {
		console.error('verifyHuman failed', e);
		return 'error';
	}
};

// The invisible check: a score token for HUMAN_CHECK_ACTION (null when blocked, slow, or not configured), traded
// for a pass. The server decides what a missing token means.
export const runHumanScoreCheck = async (): Promise<VerifyHumanResult> => {
	let token: string | null = null;
	if (RECAPTCHA_SCORE_SITE_KEY) {
		const enterprise: GrecaptchaEnterprise | null = await loadRecaptchaEnterprise();
		if (enterprise) {
			try {
				token = await withTimeout(enterprise.execute(RECAPTCHA_SCORE_SITE_KEY, { action: HUMAN_CHECK_ACTION }), EXECUTE_TIMEOUT_MS);
			} catch (e) {
				console.error('reCAPTCHA execute failed', e);
			}
		}
	}
	return verifyHumanToken('score', token);
};
